import React, { useState, useEffect, useRef, TouchEvent } from 'react';
import Image from '@/components/common/Image';
import { cn } from '@/utils/cn';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

interface LifeAtCobaltProps {
  title: string;
  description: string;
  images?: string[];
  className?: string;
  tag?: string;
}

const LifeAtCobalt: React.FC<LifeAtCobaltProps> = ({
  title = "Behind the Code: Life at Cobalt",
  description = "Peek into the world of Cobalt—where every day sparks creativity, collaboration, and breakthroughs.",
  images = [],
  className,
  tag = "LIFE AT COBALT",
}) => {
  // Default images if none provided
  const defaultImages = [
    "./about-us/team-1.png",
    "./about-us/team-2.png",
    "./about-us/team-3.png",
    "./about-us/team-7.png",
    "./about-us/team-9.png",
    "./about-us/team-10.png",
  ];

  const displayImages = images.length > 0 ? images : defaultImages;

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Check if screen is mobile sized
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const nextImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % displayImages.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const previousImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + displayImages.length) % displayImages.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const jumpToImage = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  // Handle touch events for swipe
  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      previousImage();
    }
  };

  return (
    <section
      className={cn("py-40 bg-black text-white relative group", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-blue-400 tracking-wider mb-4">
            {tag}
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            {title}
          </h2>
          <p className="text-lg text-gray-400">
            {description}
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative mt-12 mx-auto h-auto max-w-4xl">
          {/* Navigation Arrows */}
          {!isMobile && (
            <>
              <button
                onClick={previousImage}
                className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-blue-500/20 hover:bg-blue-500/40 text-white transition-all duration-200 transform ${
                  isHovered ? 'opacity-100 -translate-x-0' : 'opacity-0 -translate-x-4'
                } hidden md:flex items-center justify-center`}
                aria-label="Previous image"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>

              <button
                onClick={nextImage}
                className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-blue-500/20 hover:bg-blue-500/40 text-white transition-all duration-200 transform ${
                  isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                } hidden md:flex items-center justify-center`}
                aria-label="Next image"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Carousel Image */}
          <div
            className="w-full aspect-[16/10] rounded-lg overflow-hidden border border-[#242424] relative"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {displayImages.map((image, index) => (
              <div
                key={index}
                className={cn(
                  'absolute inset-0 transition-opacity duration-500',
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                )}
              >
                <Image
                  src={image}
                  alt={`Life at Cobalt ${index + 1}`}
                  className="w-full h-full object-cover"
                  width={960}
                  height={540}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {displayImages.map((_, index) => (
            <button
              key={index}
              onClick={() => jumpToImage(index)}
              className={`h-2 md:h-2 rounded-full transition-all duration-200 ${
                currentIndex === index
                  ? 'bg-blue-400 w-6 md:w-6'
                  : 'bg-gray-600 hover:bg-gray-500 w-2'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LifeAtCobalt;